/**
 * ErrorBoundary — catches uncaught render-tree errors and shows a useful
 * fallback instead of a blank white page.
 *
 * Without this, a thrown error inside any descendant (e.g. SldSymbolNode
 * dereferencing undefined, or a stale-state issue mid-drag) takes down
 * the whole React root and the editor goes blank with no diagnostic.
 *
 * The fallback shows the error message + stack and offers a Reload button
 * (full page reload) and a Reset button (restore from the last preset),
 * so the user can recover without losing the page.
 *
 * NOTE: error boundaries only catch errors thrown during RENDER, in
 * lifecycle methods, and in constructors. They do NOT catch errors
 * inside event handlers, async code, or setTimeout callbacks. For
 * those, surround with try/catch and report manually.
 */
import React from 'react';

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log so devs can grab from the console even after the user clicks Reload.
    // eslint-disable-next-line no-console
    console.error('SLD ErrorBoundary caught:', error, info);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '32px 40px',
          maxWidth: 880,
          margin: '40px auto',
          background: '#fff',
          border: '1px solid #fca5a5',
          borderRadius: 12,
          color: '#0f172a'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: '#fee2e2', color: '#b91c1c',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 800
            }}
          >!</div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
            The editor hit an unexpected error
          </h1>
        </div>
        <p style={{ margin: '0 0 14px', color: '#475569', fontSize: 14 }}>
          The render tree threw and the canvas would otherwise have gone blank.
          Reload the page to recover. The full error and component stack are
          below, and also in the browser console.
        </p>
        <pre
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            overflow: 'auto',
            maxHeight: 240,
            color: '#0f172a',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
{`${error.name}: ${error.message}

${error.stack ?? '(no stack)'}

${info?.componentStack ?? ''}`}
        </pre>
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >Reload editor</button>
        </div>
      </div>
    );
  }
}
